/* eslint-disable max-classes-per-file */
import type { IUBODeclarationReason, IUBODeclarationStatus } from "@daytrip/legacy-enums";
import type { Disputes } from "mangopay2-nodejs-sdk/typings/services/Disputes";

import type { MangopayWallet } from "./MangopayWallet";

// should we use split these classes to separate files?
class IModel {
    public Id: any;

    public Tag: any;

    public CreationDate: any;

    public initialize: () => void;

    public getData: (attribute: string) => any;

    public setData: (attribute: string | Object, value?: string) => any;

    public getReadOnlyProperties: () => Array<any>;

    public getDependsObjects: () => Array<any>;

    public parse: () => void;
}

class IEntityBase extends IModel {
    public getReadOnlyProperties: () => any;

    public toJSON: () => Object;

    public parse: () => void;
}

class ICard extends IEntityBase {
    public ExpirationDate: any;

    public Alias: any;

    public CardProvider: any;

    public UserId: any;

    public CardType: any;

    public Product: any;

    public BankCode: any;

    public Country: any;

    public Active: any;

    public Currency: any;

    public Validity: any;

    public Fingerprint: any;
}

export interface IMangopay {
    Users: IUsers;
    Wallets: IWallets;
    Cards: ICards;
    CardRegistrations: ICardRegistrations;
    Clients: IClients;
    PayIns: IPayIns;
    Refunds: IRefunds;
    Transfers: ITransfers;
    PayOuts: IPayOuts;
    Events: IEvents;
    UboDeclarations: IUBODeclarations;
    Reports: IReports;
    KycDocuments: IKycDocuments;
    Disputes: Disputes;
}

export interface IUser {
    PersonType: string;
    Email: string;
    KYCLevel: string;
    Id: string;
    Tag: string;
    CreationDate: number;
}

export interface IMangopayNaturalUser {
    Address: IMangopayAddress;
    FirstName: string;
    LastName: string;
    Birthday: number;
    Nationality: string;
    CountryOfResidence: string;
    Occupation: string;
    IncomeRange: number;
    ProofOfIdentity: string;
    ProofOfAddress: string;
    Capacity: string;
    PersonType: string;
    Email: string;
    KYCLevel: string;
    Id: string;
    Tag: string;
    CreationDate: number;
}

export interface IMangopayLegalUser extends IMangopayNaturalUser {
    HeadquartersAddress: IMangopayAddress;
    LegalPersonType: MangopayLegalPersonType;
    Name: string;
    LegalRepresentativeAddress: IMangopayAddress;
    LegalRepresentativeBirthday: number;
    LegalRepresentativeCountryOfResidence: string;
    LegalRepresentativeNationality: string;
    LegalRepresentativeEmail: string;
    LegalRepresentativeFirstName: string;
    LegalRepresentativeLastName: string;
    Email: string;
}

interface IMangopayAddress {
    AddressLine1: string;
    AddressLine2: string;
    City: string;
    Region: string;
    PostalCode: string;
    Country: string;
}

export class IKycDocuments {
    public get: (documentId: string) => Promise<any>;
}

export interface KycDocument {
    CreationDate: string;
    Id: string;
    ProcessedDate: number;
    RefusedReasonMessage: string;
    RefusedReasonType: string;
    Status: string;
    Tag: any;
    Type: string;
    UserId: string;
}

type MangopayLegalPersonType = "BUSINESS" | "SOLETRADER";

type MangopayUserCategory = "PAYER" | "OWNER";

type MangopayPersonType = "LEGAL" | "NATURAL";

export interface IUBODeclaration {
    Id: string;
    UserId: string;
    CreationDate: number;
    ProcessedDate: number;
    Status: IUBODeclarationStatus;
    Reason: IUBODeclarationReason;
    Message: string;
    Ubos: Array<IUBO>;
}

export interface IBirthplace {
    City: string;
    Country: string;
}

export interface IUBO {
    Id?: string;
    FirstName?: string;
    LastName?: string;
    Address?: IMangopayAddress;
    Nationality?: string;
    Birthday?: number;
    Birthplace?: IBirthplace;
    IsActive?: boolean;
}

export type BankAccountType = "IBAN" | "GB" | "US" | "CA" | "OTHER";

export interface IBankAccount {
    Id: string;
    Type: BankAccountType;
    OwnerAddress: IMangopayAddress;
    Country?: string;
    OwnerName: string;
    UserId: string;
    Active: boolean;
    CreationDate: number;
    IBAN: string;
    BIC: string;
    ABA: string;
}

export type CreateRegularUserOptions = {
    Email: string;
    FirstName: string;
    LastName: string;
    Birthday: number;
    Nationality: string;
    CountryOfResidence: string;
    Tag: string;
    PersonType: MangopayPersonType;
    Occupation?: string;
    IncomeRange?: number;
    Address?: IMangopayAddress;
    UserCategory: MangopayUserCategory;
    TermsAndConditionsAccepted: boolean;
};

export type CreateLegalUserOptions = {
    Email: string;
    Tag: string;
    Name?: string;
    HeadquartersAddress: IMangopayAddress;
    LegalPersonType?: MangopayLegalPersonType;
    LegalRepresentativeAddress?: IMangopayAddress;
    LegalRepresentativeBirthday?: number;
    LegalRepresentativeCountryOfResidence?: string;
    LegalRepresentativeNationality?: string;
    LegalRepresentativeEmail?: string;
    LegalRepresentativeFirstName?: string;
    LegalRepresentativeLastName?: string;
    CompanyNumber?: string; // if LegalPersonType BUSINESS is mandatory
    UserCategory: MangopayUserCategory;
    TermsAndConditionsAccepted: boolean;
};

class IUsers {
    public create: (
        options: CreateRegularUserOptions | CreateLegalUserOptions,
    ) => IMangopayNaturalUser | IMangopayLegalUser;

    public update: any;

    public get: any;

    public getAll: any;

    public getCards: (userId: string) => Array<ICard>;

    public getLegal: any;

    public getKycDocument: any;

    public createKycDocument: any;

    public updateKycDocument: any;

    public createKycPage: any;

    public createBankAccount: any;

    public deactivateBankAccount: any;

    public getBankAccount: any;

    public getBankAccounts: (userId: string) => Array<IBankAccount>;

    public cards: any;

    public getWallets: (userId: string) => MangopayWallet[];
}

class IWallets {
    public create: any;

    public get: any;
}

type MangopayFundsType = "DEAFULT" | "FEES" | "CREDIT";

class IClients {
    public getClientWallet: (fundsType: MangopayFundsType, currency: string) => MangopayWallet;
}

class ICards {
    public get: any;

    public cards: Array<ICard>;

    public update: any;
}

class ICardRegistrations {
    public update: any;

    public create: any;
}

class IPayIns {
    public create: any;

    public get: any;

    public createRefund: any;
}

class IRefunds {
    public get: any;
}

class ITransfers {
    public create: any;

    public createRefund: any;
}

class IPayOuts {
    public create: any;

    public get: any;
}

class IEvents {
    public getAll: any;
}

class IUBODeclarations {
    public create: (userId: string, callback?: () => void, options?: any) => Promise<IUBODeclaration>;

    public get: (userId: string, id: string, callback?: () => void, options?: any) => Promise<IUBODeclaration>;

    public getAll: (userId: string, callback?: () => void, options?: any) => Promise<Array<IUBODeclaration>>;

    public update: (
        userId: string,
        uboDeclaration: IUBODeclaration,
        callback?: () => void,
        options?: any,
    ) => Promise<IUBODeclaration>;

    public createUbo: (
        userId: string,
        uboDeclarationId: string,
        ubo: IUBO,
        callback?: () => void,
        options?: any,
    ) => Promise<IUBO>;

    public getUbo: (
        userId: string,
        uboDeclarationId: string,
        uboId: string,
        callback?: () => void,
        options?: any,
    ) => Promise<IUBO>;

    public updateUbo: (
        userId: string,
        uboDeclarationId: string,
        ubo: IUBO,
        callback?: () => void,
        options?: any,
    ) => void;
}

class IReports {
    public create: (options: any) => Promise<Report>;

    public get: (reportId: string) => Promise<Report>;
}

export type Report = {
    CallbackURL: string | null;
    Columns: string[];
    CreationDate: number;
    DownloadFormat: "csv";
    DownloadURL: string | null;
    Id: string;
    Preview: boolean;
    ReportDate: number;
    ReportType: "WALLETS" | "TRANSACTIONS";
    ResultCode: string;
    ResultMessage: string;
    Sort: string;
    Status: "CREATED" | "SUCCEEDED" | "FAILED" | "READY_FOR_DOWNLOAD" | "PENDING";
    Tag: null;
};
